import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ApiError, getWebInstallationId } from '../../api/client';
import type { EmailRegistrationRequest } from '../../api/contract';
import { captureAuthEpoch } from '../../app/auth-context';
import { authMutation, createAuthRequestKey } from './api';
import { safeReturnTo } from './returnTo';
import './auth.css';

export function EmailRequestPage() {
  const reset = useLocation().pathname.endsWith('/reset');
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'));
  const [epoch] = useState(captureAuthEpoch);
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [retryAt, setRetryAt] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const attempt = useRef<{ email: string; key: string } | undefined>(undefined);
  const submitting = useRef(false);
  useEffect(() => { document.title = `${reset ? 'Сбросить пароль' : 'Регистрация по почте'} — Казна`; }, [reset]);
  useEffect(() => {
    if (!retryAt) return;
    const timeout = window.setTimeout(() => setRetryAt(0), Math.min(Math.max(0, retryAt - Date.now()), 2_147_483_647));
    return () => window.clearTimeout(timeout);
  }, [retryAt]);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting.current || retryAt > Date.now()) return;
    // The server accepts ASCII local parts with IDN domains, including .рф.
    // Native email/typeMismatch validation is intentionally not authoritative.
    if (!email) { setInvalid(true); setError('Введите адрес электронной почты.'); input.current?.focus(); return; }
    if (attempt.current?.email !== email) attempt.current = { email, key: createAuthRequestKey() };
    submitting.current = true; setPending(true); setError(''); setInvalid(false);
    try {
      const body: EmailRegistrationRequest = { email, installationId: getWebInstallationId() };
      await authMutation(`/auth/web/email/${reset ? 'password-reset' : 'registration'}-requests`, body, attempt.current.key, epoch);
      setSent(true); setRetryAt(Date.now() + 60_000); attempt.current = undefined;
    } catch (caught) {
      setError(caught instanceof ApiError && caught.status === 429 ? 'Слишком много запросов. Дождитесь, когда кнопка станет доступна.' : caught instanceof ApiError && caught.status === 503 ? 'Вход по почте временно недоступен. Попробуйте позже.' : caught instanceof ApiError && caught.status === 0 ? 'Не удалось отправить запрос: проверьте соединение и повторите попытку.' : caught instanceof Error ? caught.message : 'Не удалось отправить письмо.');
      if (caught instanceof ApiError && caught.retryAfterMs !== undefined) setRetryAt(Date.now() + caught.retryAfterMs);
    } finally { submitting.current = false; setPending(false); }
  };
  return <main className="login-page public-auth-page email-request-page">
    <section className="login-card" aria-labelledby="email-request-title">
      <img className="brand-mark large" src="/icon.svg" alt="" aria-hidden="true" />
      <h1 id="email-request-title">{reset ? 'Сбросить пароль' : 'Регистрация по почте'}</h1>
      <p>{reset ? 'Отправим ссылку для нового пароля, если для этой почты есть аккаунт.' : 'Отправим письмо с дальнейшими шагами. Пароль задаётся после подтверждения почты.'}</p>
      <form className="form" noValidate onSubmit={event => void submit(event)} aria-busy={pending}>
        <label className="field" htmlFor="email-request-address"><span>Электронная почта</span>
          <input ref={input} id="email-request-address" name="email" type="text" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} required
            value={email} readOnly={pending} aria-invalid={invalid} aria-describedby={error ? 'email-request-error' : undefined}
            onChange={event => { setEmail(event.target.value); setInvalid(false); setSent(false); attempt.current = undefined; }} />
        </label>
        {error && <p className="error" id="email-request-error" role="alert">{error}</p>}
        {sent && <p role="status">Запрос принят. Проверьте почту и папку «Спам». Повторное письмо можно запросить через минуту.</p>}
        {!sent && retryAt > 0 && <p role="status">Повторите запрос после окончания паузы.</p>}
        <button className="button button-primary wide" type="submit" disabled={pending || retryAt > 0}>{pending ? 'Отправляем…' : 'Отправить письмо'}</button>
        <Link className="auth-text-link auth-return-link" to={`/auth/email/login${returnTo === '/dashboard' ? '' : `?returnTo=${encodeURIComponent(returnTo)}`}`}>Войти по почте</Link>
      </form>
    </section>
  </main>;
}
